import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { CContainer, CButton, CBadge, CSpinner } from '@coreui/react';
import api from '../../api/axiosInstance';
import html2pdf from 'html2pdf.js';

// KaTeX for LaTeX Math Rendering
import 'katex/dist/katex.min.css';
import katex from 'katex';

// Clean LaTeX and HTML parser helper
const renderMathContent = (htmlContent) => {
  if (!htmlContent) return '';
  
  // Clean HTML entity noise like &nbsp; before parsing LaTeX
  let cleaned = htmlContent
    .replace(/&nbsp;/gi, ' ')
    .replace(/&amp;/gi, '&')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>');

  // Render Inline ($...$) and Display ($$...$$) Math Expressions
  return cleaned.replace(/\$\$(.*?)\$\$|\$(.*?)\$/g, (match, displayMath, inlineMath) => {
    const math = displayMath || inlineMath;
    try {
      return katex.renderToString(math, {
        displayMode: !!displayMath,
        throwOnError: false
      });
    } catch (e) {
      return match;
    }
  });
};

const getTruncatedText = (htmlContent, maxLength = 50) => {
  if (!htmlContent) return '';
  const doc = new DOMParser().parseFromString(htmlContent, 'text/html');
  const plainText = doc.body.textContent || "";
  return plainText.trim().substring(0, maxLength);
};

const createCleanSlug = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return '';
  return rawUrl
    .replace(/<[^>]*>/g, '')
    .replace(/%C2%A0/g, '-')
    .replace(/%20/g, '-')
    .replace(/[\/\\]/g, '-')
    .replace(/[^a-zA-Z0-9\s-]/g, '')
    .replace(/\s+/g, '-')
    .replace(/-+/g, '-')
    .replace(/^-+|-+$/g, '');
};

const YearSpecificQuestionBank = () => {
  const { section, title, year } = useParams();
  const navigate = useNavigate();

  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const cleanTitle = title ? title.replace(/-/g, ' ') : '';

  useEffect(() => {
    window.scrollTo(0, 0);
    fetchAllYearQuestions();
  }, [section, title, year]);

  const fetchAllYearQuestions = async () => {
    try {
      setLoading(true);
      setError(null);

      const res = await api.get(
        `/api/content/GetQuestionsByYear?section=${section}&category=${encodeURIComponent(cleanTitle)}&year=${year}&page=1&pageSize=1000`,
        { skipAuthCheck: true }
      );

      if (res.data) {
        setQuestions(res.data.questions || res.data.data || []);
      }
    } catch (err) {
      console.error("Error fetching year questions:", err);
      setError("Failed to load questions for this year.");
    } finally {
      setLoading(false);
    }
  };

  const handleGetSolution = (q) => {
    const rawQuestionText = q.customUrl || q.customeUrl || q.questionText || q.QuestionText || '';
    const truncatedText = getTruncatedText(rawQuestionText, 50);
    const slug = createCleanSlug(truncatedText);

    navigate(`/content/view-question/${q.id}/${slug}`);
  };

  const handleDownloadPdf = () => {
    const element = document.getElementById('printable-questions-container');
    if (!element) return;

    const discussionLinks = element.querySelectorAll('.discussion-link');
    discussionLinks.forEach(el => el.style.display = 'none');

    const opt = {
      margin:       [15, 15, 15, 15],
      filename:     `${cleanTitle.toUpperCase()}_${year}_Questions.pdf`,
      image:        { type: 'jpeg', quality: 0.98 },
      html2canvas:  { scale: 2, useCORS: true },
      jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
    };

    html2pdf().set(opt).from(element).save().then(() => {
      discussionLinks.forEach(el => el.style.display = 'inline');
    });
  };

  const handleCourseRedirect = () => {
    navigate(`/${section}/${title}/year-wise`);
  };

  if (loading) {
    return (
      <div className="text-center mt-5 py-5">
        <CSpinner color="primary" size="lg" />
        <p className="text-muted mt-2">Loading {year} Questions...</p>
      </div>
    );
  }

  if (error) {
    return (
      <CContainer className="text-center mt-5 py-4">
        <h5 className="text-danger mb-3">{error}</h5>
        <CButton color="primary" size="sm" onClick={() => navigate(-1)}>Go Back</CButton>
      </CContainer>
    );
  }

  return (
    <div style={{ overflowX: 'hidden', width: '100%' }}>
      {/* 🌟 1. FULL-WIDTH EDGE-TO-EDGE BLUE BANNER */}
      <div 
        className="text-white text-center py-5 shadow-sm mb-4" 
        style={{ 
          backgroundColor: 'rgb(0, 33, 71)',
          width: '100vw',
          position: 'relative',
          left: '50%',
          transform: 'translateX(-50%)'
        }}
      >
        <div className="container py-2 px-3">
          <h1 className="fw-bold mb-2   display-6" style={{color:'yellow'}}>
            {cleanTitle.toUpperCase()} {year} Questions
          </h1>
          <p className="lead m-0 opacity-90 text-light fs-6">
            {section.toUpperCase()} • Question Bank & Solutions
          </p>
          <div className="mt-3">
            <CBadge color="light" className="text-primary px-3 py-2 fs-6 rounded-pill shadow-xs fw-bold">
              Total Questions: {questions.length}
            </CBadge>
          </div>
        </div>
      </div>

      {/* 🚀 2. MAIN CONTENT AREA */}
      <CContainer className="pb-5"  >
        {questions.length > 0 ? (
          <div className="bg-white p-4 p-md-5 rounded-4 border shadow-sm mb-4 overflow-hidden">
            
            {/* Printable Wrapper */}
            <div id="printable-questions-container" className="p-2 w-100 overflow-hidden">
              <h4 className="fw-bold mb-4 text-center d-none d-print-block" style={{ color: '#0A2540' }}>
                {cleanTitle.toUpperCase()} - {year} Paper
              </h4>

              <ol className="ps-3 m-0 w-100">
                {questions.map((q, index) => {
                  const questionContent = q.questionText || q.QuestionText || '';
                  const renderedHtml = renderMathContent(questionContent);

                  // Extract Options from API Payload
                  const optionKeys = ['A', 'B', 'C', 'D', 'E'];
                  const availableOptions = optionKeys
                    .map(key => ({
                      key,
                      val: q[`option${key}`] || q[`Option${key}`] || ''
                    }))
                    .filter(opt => opt.val && opt.val.trim() !== '');

                  return (
                    <li 
                      key={q.id || index} 
                      className="mb-4 pb-3 text-dark question-list-item border-bottom" 
                      style={{ fontSize: '1.05rem', lineHeight: '1.7', cursor: 'pointer', wordBreak: 'break-word' }}
                      onClick={() => handleGetSolution(q)}
                    >
                      <div className="d-inline-block w-100 question-content-wrapper">
                        <span 
                          dangerouslySetInnerHTML={{ __html: renderedHtml }} 
                          className="inline-question-text"
                        />
                      </div>

                      {/* OPTIONS GRID DISPLAY */}
                      {availableOptions.length > 0 && (
                        <div className="row g-2 mt-2 ps-2 options-container">
                          {availableOptions.map((opt) => (
                            <div key={opt.key} className="col-12 col-md-6">
                              <div className="p-2 rounded border bg-light d-flex align-items-center gap-2">
                                <span className="fw-bold text-primary">({opt.key})</span>
                                <span 
                                  dangerouslySetInnerHTML={{ __html: renderMathContent(opt.val) }}
                                  className="option-text"
                                />
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </li>
                  );
                })}
              </ol>
            </div>

            {/* ACTION BUTTONS SECTION */}
            <div className="mt-5 d-flex flex-column align-items-center gap-3 mx-auto" style={{ maxWidth: '650px' }}>
              <button
                onClick={handleDownloadPdf}
                className="btn text-white fw-bold py-3 px-4 rounded-3 shadow-sm w-100 border-0"
                style={{ backgroundColor: '#0A2540', fontSize: '0.95rem', transition: 'all 0.2s' }}
                onMouseOver={(e) => e.target.style.opacity = 0.9}
                onMouseOut={(e) => e.target.style.opacity = 1}
              >
                Download All Questions of {cleanTitle.toUpperCase()} {year} Paper in PDF (Without Solutions)
              </button>

              <div className="w-100 text-center">
                <button
                  onClick={handleCourseRedirect}
                  className="btn text-white fw-bold py-3 px-4 rounded-3 shadow-sm w-100 border-0"
                  style={{ backgroundColor: '#8B0000', fontSize: '0.95rem', transition: 'all 0.2s' }}
                  onMouseOver={(e) => e.target.style.opacity = 0.9}
                  onMouseOut={(e) => e.target.style.opacity = 1}
                >
                  Get {cleanTitle.toUpperCase()} Previous Year Questions Solutions
                </button>
                <div className="text-secondary mt-2 fw-semibold" style={{ fontSize: '0.85rem' }}>
                  Need to SignUp or SignIn in {section.toUpperCase()} Course
                </div>
              </div>
            </div>

          </div>
        ) : (
          <div className="alert alert-warning text-center mt-3">No questions found for the year {year}.</div>
        )}

        {/* RESPONSIVE STYLES */}
        <style>{`
          .question-list-item {
            transition: background-color 0.2s ease;
            padding: 10px 12px;
            border-radius: 6px;
            max-width: 100%;
          }
          
          .question-list-item:hover {
            background-color: #f8f9fa;
          }

          .inline-question-text {
            max-width: 100%;
            display: inline;
          }

          .inline-question-text p, .option-text p {
            display: inline;
            margin: 0;
            padding: 0;
          }

          .question-content-wrapper img {
            max-width: 100% !important;
            height: auto !important;
            display: block;
            margin: 12px auto;
          }

          @media print {
            .d-print-block {
              display: block !important;
            }
          }
        `}</style>
      </CContainer>
    </div>
  );
};

export default YearSpecificQuestionBank;